<template>
  <div class="parametrs-list">
    <b-field horizontal v-for="(value, key) in parameters" :key="key" :class="{ hidden: value.data_type === 'HIDDEN' }">
      <template #label>
        <b-tooltip :label="value.description" type="is-dark" multilined>
          {{ ($t(value.name).title || $t(value.name) || value.name) | capitalize }}
        </b-tooltip>
      </template>

      <b-select
        v-if="value.data_type === 'SELECT'"
        :placeholder="value.description"
        v-model="items[value.name]"
        :disabled="disabled"
      >
        <option v-for="(option, option_key) in value.options" :key="option_key" :value="option.value">
          {{ option.label }}
        </option>
      </b-select>
      <b-autocomplete
        v-else-if="value.data_type === 'SELECT_CUSTOM'"
        :ref="`${key}-autocomplete`"
        v-model="items[value.name]"
        :placeholder="value.description"
        field="value"
        :data="value.options"
        :disabled="disabled"
        :open-on-focus="true"
        @select="option => (items[value.name] = option.value)"
      >
        <template #empty>{{ $t('button.add.noResultsFor') }} {{ value.name }}</template>
        <template slot-scope="props">
          {{ props.option.label }}
        </template>
      </b-autocomplete>
      <b-input
        v-else-if="value.data_type === 'STRING'"
        v-model="items[value.name]"
        :placeholder="value.description"
        :disabled="disabled"
        type="text"
        :autocomplete="value.autocomplete"
      ></b-input>
      <b-tooltip
        class="range-tooltip"
        :label="items[value.name]"
        type="is-dark"
        multilined
        v-else-if="value.data_type === 'NUM_SLIDER'"
      >
        <b-input
          type="range"
          v-model="items[value.name]"
          :placeholder="value.description"
          :disabled="disabled"
          :min="value.min_val"
          :max="value.max_val"
          :step="value.step"
          lazy
        ></b-input>
      </b-tooltip>
      <b-tooltip
        class="range-tooltip"
        :label="items[value.name]"
        type="is-dark"
        multilined
        v-else-if="value.data_type === 'PERCENT_SLIDER'"
      >
        <b-input
          type="range"
          v-model="items[value.name]"
          :placeholder="value.description"
          :disabled="disabled"
          :min="value.min_val"
          :max="value.max_val"
          :step="value.step"
          lazy
        ></b-input>
      </b-tooltip>
      <b-taginput
        v-else-if="value.data_type === 'TAGS'"
        v-model="items[value.name]"
        type="is-dark"
        close-type="is-danger"
        :placeholder="value.description"
        :disabled="disabled"
        ellipsis
      ></b-taginput>
      <section class="checkbox-group" v-else-if="value.data_type === 'BOOL_GROUP'">
        <b-checkbox
          v-for="(option, option_key) in value.options"
          v-model="items[value.name]"
          :key="option_key"
          :value="option.value"
          :native-value="option.value"
        >
          {{ option.label }}
        </b-checkbox>
      </section>
      <b-input
        v-else-if="value.data_type === 'PASSWORD'"
        :placeholder="value.description"
        v-model="items[value.name]"
        v-validate="'required'"
        icon-pack="fas"
        password-reveal
        type="password"
        :autocomplete="value.autocomplete"
      ></b-input>
      <b-input
        v-else-if="value.data_type === 'NUM'"
        v-model="items[value.name]"
        :min="value.min_val"
        :max="value.max_val"
        :placeholder="value.description"
        :step="value.step || value.min_val || 'any'"
        :disabled="disabled"
        type="number"
      ></b-input>
      <b-input
        v-else-if="value.data_type === 'TEXT'"
        v-model="items[value.name]"
        :placeholder="value.description"
        :disabled="disabled"
        type="textarea"
      ></b-input>
      <date-time-picker
        v-else-if="value.data_type === 'DATE'"
        @input="time => (items[value.name] = time)"
        :init-date="items[value.name] ? new Date(items[value.name] * 1000) : new Date()"
        :init-time="items[value.name] ? new Date(items[value.name] * 1000) : new Date()"
        :datePlaceholder="value.description"
        :disabled="disabled"
        expanded
      ></date-time-picker>
      <b-checkbox v-else-if="value.data_type === 'BOOL'" v-model="items[value.name]" :disabled="disabled"></b-checkbox>
      <div v-else-if="value.data_type === 'LINE'" class="columns">
        <div class="column is-9">
          <b-input v-model="items[value.name]" :placeholder="value.description" :disabled="disabled"></b-input>
        </div>
        <div class="column is-3 column-button">
          <button
            @click="$emit('draw', { shape: 'line', key: value.name })"
            class="button"
            :disabled="disabled || disabledDrawButtons"
          >
            {{ $t(`button.draw.title`) }}
          </button>
        </div>
      </div>
      <div v-else-if="value.data_type === 'RECTANGLE'" class="columns">
        <div class="column is-9">
          <b-input :placeholder="value.description" v-model="items[value.name]" :disabled="disabled"></b-input>
        </div>
        <div class="column is-3 column-button">
          <button
            @click="$emit('draw', { shape: 'rectangle', key: value.name })"
            class="button"
            :disabled="disabled || disabledDrawButtons"
          >
            {{ $t(`button.draw.title`) }}
          </button>
        </div>
      </div>
      <div v-else-if="value.data_type === 'POLYGON'" class="columns">
        <div class="column is-9">
          <b-input :placeholder="value.description" v-model="items[value.name]" :disabled="disabled"></b-input>
        </div>
        <div class="column is-3 column-button">
          <button
            @click="$emit('draw', { shape: 'polygon', key: value.name })"
            class="button"
            :disabled="disabled || disabledDrawButtons"
          >
            {{ $t(`button.draw.title`) }}
          </button>
        </div>
      </div>
      <div v-else-if="value.data_type === 'IMAGE'" class="columns">
        <div class="column is-9">
          <img :src="items[value.name]" />
        </div>
        <div class="column is-3 column-button">
          <input
            type="file"
            :ref="`${key}`"
            :disabled="disabled"
            style="display: none"
            @change="loadImage($refs[`${key}`][0].files[0], value.name)"
          />
          <button class="button" @click="$refs[`${key}`][0].click()" :disabled="disabled">
            {{ $t(`button.upload.title`) }}
          </button>
        </div>
      </div>
      <div v-else-if="value.data_type === 'FILE'">
        <a :href="items[value.name]">{{ items[value.name] }}</a>
        <b-upload @input="handleUpload($event, value.name)" :accept="value.mime" drag-drop :disabled="disabled">
          {{ $t('button.upload.file', { types: value.types }) }}
          <b-loading :active.sync="loading" :is-full-page="false"/>
        </b-upload>
      </div>
      <div v-else-if="value.data_type === 'STREAM'" class="columns">
        <div class="column is-9">
          <img :src="items[value.name]" />
        </div>
        <div class="column is-3 column-button">
          <button class="button" :disabled="disabled">{{ value.description }}</button>
        </div>
      </div>
      <b-input
        v-else
        v-model="items[value.name]"
        :placeholder="value.description"
        :disabled="disabled || value.data_type === 'DISABLED'"
      />
    </b-field>
  </div>
</template>

<script>
import DateTimePicker from '@/components/DateTimePicker'
export default {
  name: 'ParametrsList',
  components: {
    DateTimePicker
  },
  filters: {
    capitalize: function(value) {
      if (!value) return ''
      value = value.toString()
      return value.charAt(0).toUpperCase() + value.slice(1)
    }
  },
  props: {
    items: {
      type: Object,
      default() {
        return {}
      }
    },
    parameters: {
      type: Array,
      default() {
        return []
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    disabledDrawButtons: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false
    }
  },
  methods: {
    loadImage(data, itemKey) {
      let img = new Image()
      img.src = URL.createObjectURL(data)
      let vm = this
      img.onload = function() {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')
        canvas.width = img.width
        canvas.height = img.height
        ctx.drawImage(img, 0, 0)
        let dataURL = canvas.toDataURL()
        vm.items[itemKey] = dataURL
      }
    },
    handleUpload(file, field) {
      const url = `${process.env.VUE_APP_API_ENDPOINT || window.location.origin}/upload-file`
      const body = new FormData()
      body.append('file', file)
      body.append('purpose', this.$route.name)
      this.loading = true
      return fetch(url, {
        credentials: 'include',
        body,
        method: 'POST'
      })
        .then(res => {
          if (!res.ok) {
            throw res.json()
          }
          return res.json()
        })
        .then(data => {
          this.items[field] = data.name
        })
        .catch(error =>
          error.then(data => {
            this.$notify({
              message: JSON.parse(data.message).message,
              type: 'danger'
            })
          })
        )
        .finally(() => {
          this.loading = false
        })
    }
  }
}
</script>

<style lang="scss">
.parametrs-list {
  .field {
    &.hidden {
      display: none;
    }
    .field-label {
      min-width: 8rem;
      padding-top: 0;
      width: 100%;
      .label {
        text-align: right;
        width: 100%;
        min-width: 6.25rem;
      }
    }
    .control {
      font-size: 1.25em;
      &.autocomplete {
        .dropdown-menu {
          max-width: unset;
          .dropdown-content .dropdown-item {
            padding-right: 1rem;
            .add {
              width: 100%;
              display: block;
            }
          }
        }
      }
      .select {
        width: 100%;
        height: 1.5rem;
        select {
          width: 100%;
          height: 1.5rem;
          padding-top: 0;
          padding-bottom: 0;
          font-size: 0.9rem;
          padding-left: 0.25rem;
          padding-right: 0.25rem;
          &::after {
            right: 0.9rem;
          }
        }
        &:not(.is-multiple):not(.is-loading)::after {
          right: 0.9rem;
        }
      }
      &.datepicker,
      &.timepicker {
        .select {
          vertical-align: middle;
        }
        .icon {
          font-size: 0.9rem;
          height: 1.5rem;
        }
      }
      &.has-icons-right {
        .icon {
          font-size: 0.6875rem;
          right: 0.3125rem;
        }
      }
    }
    .upload.control {
      width: 100%;
      .upload-draggable {
        width: 100%;
        font-size: 0.9em;
      }
    }
    input {
      font-size: 0.9rem;
      height: 1.5rem;
    }
    textarea {
      font-size: 0.9rem;
    }
    .columns {
      margin-right: 0;
      .column-button {
        padding-right: 0;
        padding-left: 0;
        .button {
          width: 100%;
          color: #b032e7;
          border-color: #b032e7;
          font-size: 0.9rem;
          height: 1.5rem;
        }
      }
    }
    .checkbox-group {
      display: flex;
      flex-wrap: wrap;
      gap: 0.75rem;
      .checkbox {
        margin: 0;
      }
    }
    .range-tooltip {
      width: 100%;
      .control {
        width: 100%;
      }
      &::before {
        bottom: calc(100%);
      }
      &::after {
        width: min-content !important;
        bottom: calc(100% + 4px);
      }
      input[type='range'] {
        height: 23px;
        -webkit-appearance: none;
        margin: 0;
        width: 100%;
      }
      input[type='range']:focus {
        outline: none;
      }
      input[type='range']::-webkit-slider-runnable-track {
        width: 100%;
        height: 4px;
        cursor: pointer;
        animate: 0.2s;
        box-shadow: 0 0 0 #000000;
        background: #3273dc;
        border-radius: 50px;
        border: 0 solid #010101;
      }
      input[type='range']::-webkit-slider-thumb {
        box-shadow: 0 0 0 #000031;
        border: 1px solid #001e06;
        height: 16px;
        width: 16px;
        border-radius: 16px;
        background: #ffffff;
        cursor: pointer;
        -webkit-appearance: none;
        margin-top: -6px;
      }
      input[type='range']:focus::-webkit-slider-runnable-track {
        background: #3273dc;
      }
      input[type='range']::-moz-range-track {
        width: 100%;
        height: 8px;
        cursor: pointer;
        animate: 0.2s;
        box-shadow: 0 0 0 #000000;
        background: #3273dc;
        border-radius: 50px;
        border: 0 solid #010101;
      }
      input[type='range']::-moz-range-thumb {
        box-shadow: 0 0 0 #000031;
        border: 1px solid #001e06;
        height: 16px;
        width: 16px;
        border-radius: 16px;
        background: #ffffff;
        cursor: pointer;
      }
      input[type='range']::-ms-track {
        width: 100%;
        height: 8px;
        cursor: pointer;
        animate: 0.2s;
        background: transparent;
        border-color: transparent;
        color: transparent;
      }
      input[type='range']::-ms-fill-lower {
        background: #3273dc;
        border: 0 solid #010101;
        border-radius: 100px;
        box-shadow: 0 0 0 #000000;
      }
      input[type='range']::-ms-fill-upper {
        background: #3273dc;
        border: 0 solid #010101;
        border-radius: 100px;
        box-shadow: 0 0 0 #000000;
      }
      input[type='range']::-ms-thumb {
        margin-top: 1px;
        box-shadow: 0 0 0 #000031;
        border: 1px solid #001e06;
        height: 16px;
        width: 16px;
        border-radius: 16px;
        background: #ffffff;
        cursor: pointer;
      }
      input[type='range']:focus::-ms-fill-lower {
        background: #3273dc;
      }
      input[type='range']:focus::-ms-fill-upper {
        background: #3273dc;
      }
      .input {
        border: 0;
        box-shadow: unset;
      }
    }
  }
}
</style>
